// Route-tree wiring only.
//   - Loaders live in `src/lib/route-loaders.ts` (prefetch helpers, `defaultPreload: "intent"`).
//   - Guards live in `src/components/guards/` (`AuthGuard`, `GuestGuard`, `AdminGuard`,
//     `OnboardingGuard`, `TermsGuard`).
//   - Lazy page imports live in `src/routes/lazy-pages.ts`.
// Do not inline business logic, data-fetching, or guard implementations in this file —
// add them to the modules above and reference them here. Audited 2026-05-15.
import {
	createRootRoute,
	createRoute,
	Outlet,
	redirect,
} from "@tanstack/react-router";
import { Suspense } from "react";
import {
	AdminGuard,
	AuthGuard,
	GuestGuard,
	OnboardingGuard,
	TermsGuard,
} from "./components/guards";
import {
	forgetListSearchOutside,
	LIST_FILTER_WRITE,
	parseAdminListSearch,
	restoreListSearch,
} from "./lib/admin-list-search";
import { AdminLayout } from "./components/layout/admin-layout";
import { DashboardLayout } from "./components/layout/dashboard-layout";
import {
	prefetchAdminBlogCategories,
	prefetchAdminBlogEdit,
	prefetchAdminBlogSuggestions,
	prefetchAdminBlogs,
	prefetchAdminDashboard,
	prefetchAdminProAnalytics,
	prefetchAdminProCompanyProfile,
	prefetchAdminProDetail,
	prefetchAdminProjects,
	prefetchAdminPros,
	prefetchAdminRoomCategories,
	prefetchAdminTaxonomy,
	prefetchAdminUserDetail,
	prefetchAdminUsers,
	prefetchAnalytics,
	prefetchBlogEdit,
	prefetchBlogFeatures,
	prefetchBlogs,
	prefetchCreateReel,
	prefetchCrmAnalytics,
	prefetchCrmKanban,
	prefetchCrmLeadDetail,
	prefetchCrmSettings,
	prefetchDashboard,
	prefetchNotifications,
	prefetchProfile,
	prefetchProjectDetail,
	prefetchProjects,
	prefetchSettings,
	prefetchSocialDraft,
	prefetchSocialDrafts,
	prefetchTeam,
	prefetchWebsite,
} from "./lib/route-loaders";
import {
	AcceptInvitationPage,
	AcceptTermsPage,
	AdminAnalyticsPage,
	AdminBlogCategoriesPage,
	AdminBlogCreatePage,
	AdminBlogEditPage,
	AdminBlogListPage,
	AdminBlogSuggestionsPage,
	AdminCalculatorConfigPage,
	AdminCommunicationsPage,
	AdminConsultationsPage,
	AdminWebLeadsPage,
	AdminDashboardPage,
	AdminFeedbackPage,
	AdminHomePagePage,
	AdminImportsDetailPage,
	AdminImportsNewPage,
	AdminImportsPage,
	AdminNotificationTestPage,
	AdminPartnersPage,
	AdminPayoutsPage,
	AdminProAnalyticsPage,
	AdminProCompanyProfilePage,
	AdminProDetailPage,
	AdminProjectDetailPage,
	AdminProjectsPage,
	AdminProsPage,
	AdminReferralFunnelPage,
	AdminReferralQueuePage,
	AdminReferralsPage,
	AdminRoomCategoriesPage,
	AdminRoomCategoryDetailPage,
	AdminRrmDashboardPage,
	AdminRrmInboxPage,
	AdminRrmProspectDetailPage,
	AdminRrmProspectsPage,
	AdminRrmRampPage,
	AdminRrmSignupsPage,
	AdminRrmTasksPage,
	AdminSearchPage,
	AdminSocialStudioMusicPage,
	AdminSocialStudioTemplatesPage,
	AdminTaxonomyPage,
	AdminUserDetailPage,
	AdminUserSecurityPage,
	AdminUsersPage,
	AdminWhatsAppCampaignCreatePage,
	AdminWhatsAppCampaignsPage,
	AdminWhatsAppDashboardPage,
	AdminWhatsAppInboxPage,
	AdminWhatsAppSendPage,
	AdminWhatsAppTemplatesPage,
	AnalyticsPage,
	AuthPage,
	CreateReelPage,
	CrmAnalyticsPage,
	CrmLeadDetailPage,
	CrmPage,
	CrmSettingsPage,
	DashboardPage,
	EditBlogPage,
	InternalMetricsPage,
	NewBlogPage,
	NotificationsPage,
	OnboardingPage,
	ProBlogsPage,
	ProfilePage,
	ProjectDetailsPage,
	ProjectsPage,
	ProMyBlogsPage,
	ResetPasswordPage,
	SecuritySettingsPage,
	SettingsPage,
	SocialDraftDetailPage,
	SocialStudioPage,
	TeamPage,
	VerifyEmailPage,
	WebsitePage,
} from "./routes/lazy-pages";

// Content-area loading fallback (NOT full-screen -- layout is already mounted)
const ContentLoading = () => (
	<div className="flex items-center justify-center h-64">
		<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
	</div>
);

// Full-screen loading fallback (for standalone routes without a parent layout)
const FullScreenLoading = () => (
	<div className="flex items-center justify-center h-screen">
		<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
	</div>
);

// ─── Root Route ──────────────────────────────────────────────────────────────
export const rootRoute = createRootRoute({
	component: () => <Outlet />,
});

// ─── Pro Layout Route (pathless) ──────────────────────────────────────────
// AuthGuard + OnboardingGuard + DashboardLayout stay mounted across all pro routes.
// Only the <Outlet /> content area swaps on navigation.
const proLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_proLayout",
	component: () => (
		<AuthGuard>
			<TermsGuard>
				<OnboardingGuard>
					<DashboardLayout>
						<Suspense fallback={<ContentLoading />}>
							<div className="animate-page-in">
								<Outlet />
							</div>
						</Suspense>
					</DashboardLayout>
				</OnboardingGuard>
			</TermsGuard>
		</AuthGuard>
	),
});

// ─── Guest Layout Route (pathless) ───────────────────────────────────────────
const guestLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_guestLayout",
	component: () => (
		<GuestGuard>
			<Suspense fallback={<FullScreenLoading />}>
				<Outlet />
			</Suspense>
		</GuestGuard>
	),
});

// ─── Admin Layout Route (pathless) ───────────────────────────────────────────
const adminLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "_adminLayout",
	beforeLoad: ({ location, cause }) =>
		forgetListSearchOutside(location.pathname, cause),
	component: () => (
		<AdminGuard>
			<AdminLayout>
				<Suspense fallback={<ContentLoading />}>
					<div className="animate-page-in">
						<Outlet />
					</div>
				</Suspense>
			</AdminLayout>
		</AdminGuard>
	),
});

// ─── Pro Child Routes ─────────────────────────────────────────────────────
// No guards or layout wrappers needed -- parent layout provides them.

export const indexRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/",
	loader: () => prefetchDashboard(),
	component: () => <DashboardPage />,
});

export const projectsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/projects",
	loader: () => prefetchProjects(),
	component: () => <ProjectsPage />,
});

export const projectDetailsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/projects/$projectId",
	validateSearch: (search) => ({
		tab: (search.tab as string) || "",
	}),
	loader: ({ params }) => prefetchProjectDetail(params.projectId),
	component: () => <ProjectDetailsPage />,
});

export const profileRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/profile",
	validateSearch: (search) => ({
		tab: (search.tab as string) || "basics",
	}),
	loader: () => prefetchProfile(),
	component: () => <ProfilePage />,
});

export const settingsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/settings",
	loader: () => prefetchSettings(),
	component: () => <SettingsPage />,
});

export const securitySettingsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/settings/security",
	component: () => <SecuritySettingsPage />,
});

export const teamRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/team",
	loader: () => prefetchTeam(),
	component: () => <TeamPage />,
});

export const analyticsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/analytics",
	loader: () => prefetchAnalytics(),
	component: () => <AnalyticsPage />,
});

export const proBlogsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/blogs",
	loader: () => prefetchBlogs(),
	component: () => <ProMyBlogsPage />,
});

export const proBlogFeaturesRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/blog-features",
	loader: () => prefetchBlogFeatures(),
	component: () => <ProBlogsPage />,
});

export const proNewBlogRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/blogs/new",
	component: () => <NewBlogPage />,
});

export const proEditBlogRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/blogs/$id/edit",
	loader: ({ params }) => prefetchBlogEdit(params.id),
	component: () => <EditBlogPage />,
});

export const websiteRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/website",
	loader: () => prefetchWebsite(),
	component: () => <WebsitePage />,
});

export const crmRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/crm",
	loader: () => prefetchCrmKanban(),
	component: () => <CrmPage />,
});

export const crmLeadDetailRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/crm/leads/$leadId",
	loader: ({ params }) => prefetchCrmLeadDetail(params.leadId),
	component: () => <CrmLeadDetailPage />,
});

export const crmAnalyticsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/crm/analytics",
	loader: () => prefetchCrmAnalytics(),
	component: () => <CrmAnalyticsPage />,
});

export const crmSettingsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/crm/settings",
	loader: () => prefetchCrmSettings(),
	component: () => <CrmSettingsPage />,
});

export const notificationsRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/notifications",
	loader: () => prefetchNotifications(),
	component: () => <NotificationsPage />,
});

export const socialStudioRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/social-studio",
	validateSearch: (search) => ({
		highlight: (search.highlight as string) || undefined,
	}),
	loader: () => prefetchSocialDrafts(),
	component: () => <SocialStudioPage />,
});

export const socialDraftDetailRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/social-studio/$draftId",
	loader: ({ params }) => prefetchSocialDraft(params.draftId),
	component: () => <SocialDraftDetailPage />,
});

export const createReelRoute = createRoute({
	getParentRoute: () => proLayoutRoute,
	path: "/social-studio/create",
	validateSearch: (search) => ({
		projectId: (search.projectId as string) || undefined,
	}),
	loader: () => prefetchCreateReel(),
	component: () => <CreateReelPage />,
});

// ─── Guest Child Routes ──────────────────────────────────────────────────────

export const loginRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/login",
	validateSearch: (search) => ({
		redirect: (search.redirect as string) || undefined,
	}),
	beforeLoad: ({ search }) => {
		throw redirect({ to: "/auth/sign-in", search });
	},
});

export const registerRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/register",
	validateSearch: (search) => ({
		email: (search.email as string) || undefined,
		redirect: (search.redirect as string) || undefined,
		token: (search.token as string) || undefined,
	}),
	beforeLoad: ({ search }) => {
		throw redirect({ to: "/auth/sign-up", search });
	},
});

export const forgotPasswordRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/forgot-password",
	beforeLoad: () => {
		throw redirect({ to: "/auth/forgot-password" });
	},
});

export const resetPasswordRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/reset-password",
	component: () => <ResetPasswordPage />,
});

export const verifyEmailRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/verify-email",
	component: () => <VerifyEmailPage />,
});

function AuthPageWrapper() {
	const { pathname } = authRoute.useParams();
	const search = authRoute.useSearch();
	return (
		<AuthPage
			pathname={pathname}
			search={search as Record<string, string | undefined>}
		/>
	);
}

export const authRoute = createRoute({
	getParentRoute: () => guestLayoutRoute,
	path: "/auth/$pathname",
	validateSearch: (search) => ({
		redirect: (search.redirect as string) || undefined,
		email: (search.email as string) || undefined,
		token: (search.token as string) || undefined,
		error: (search.error as string) || undefined,
	}),
	component: () => <AuthPageWrapper />,
});

// ─── Admin Child Routes ──────────────────────────────────────────────────────

export const adminDashboardRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin",
	loader: () => prefetchAdminDashboard(),
	component: () => <AdminDashboardPage />,
});

export const adminProsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/pros",
	validateSearch: parseAdminListSearch,
	beforeLoad: ({ search, cause, location }) =>
		restoreListSearch(
			"/admin/pros",
			search,
			cause,
			LIST_FILTER_WRITE in location.state,
		),
	loader: () => prefetchAdminPros(),
	component: () => <AdminProsPage />,
});

export const adminProDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/pros/$proId",
	loader: ({ params }) => prefetchAdminProDetail(params.proId),
	component: () => <AdminProDetailPage />,
});

export const adminProAnalyticsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/pros/$proId/analytics",
	loader: ({ params }) => prefetchAdminProAnalytics(params.proId),
	component: () => <AdminProAnalyticsPage />,
});

export const adminProCompanyProfileRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/pros/$proId/company-profile",
	loader: ({ params }) => prefetchAdminProCompanyProfile(params.proId),
	component: () => <AdminProCompanyProfilePage />,
});

export const adminProjectsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/projects",
	validateSearch: parseAdminListSearch,
	beforeLoad: ({ search, cause, location }) =>
		restoreListSearch(
			"/admin/projects",
			search,
			cause,
			LIST_FILTER_WRITE in location.state,
		),
	loader: () => prefetchAdminProjects(),
	component: () => <AdminProjectsPage />,
});

export const adminImportsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/imports",
	component: () => <AdminImportsPage />,
});

export const adminImportsNewRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/imports/new",
	component: () => <AdminImportsNewPage />,
});

export const adminImportsDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/imports/$importId",
	component: () => <AdminImportsDetailPage />,
});

export const adminProjectDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/projects/$projectId",
	component: () => <AdminProjectDetailPage />,
});

export const adminUsersRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/users",
	loader: () => prefetchAdminUsers(),
	component: () => <AdminUsersPage />,
});

export const adminUserDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/users/$userId",
	loader: ({ params }) => prefetchAdminUserDetail(params.userId),
	component: () => <AdminUserDetailPage />,
});

export const adminUserSecurityRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/users/$userId/security",
	component: () => <AdminUserSecurityPage />,
});

export const adminTaxonomyRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/taxonomy",
	loader: () => prefetchAdminTaxonomy(),
	component: () => <AdminTaxonomyPage />,
});

export const adminAnalyticsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/analytics",
	component: () => <AdminAnalyticsPage />,
});

export const adminCommunicationsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/communications",
	component: () => <AdminCommunicationsPage />,
});

export const adminFeedbackRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/feedback",
	component: () => <AdminFeedbackPage />,
});

export const adminConsultationsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/consultations",
	component: () => <AdminConsultationsPage />,
});

export const adminWebLeadsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/web-leads",
	component: () => <AdminWebLeadsPage />,
});

export const adminBlogsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/blogs",
	loader: () => prefetchAdminBlogs(),
	component: () => <AdminBlogListPage />,
});

export const adminBlogCreateRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/blogs/create",
	component: () => <AdminBlogCreatePage />,
});

export const adminBlogEditRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/blogs/$id/edit",
	loader: ({ params }) => prefetchAdminBlogEdit(params.id),
	component: () => <AdminBlogEditPage />,
});

export const adminBlogCategoriesRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/blogs/categories",
	loader: () => prefetchAdminBlogCategories(),
	component: () => <AdminBlogCategoriesPage />,
});

export const adminBlogSuggestionsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/blogs/suggestions",
	loader: () => prefetchAdminBlogSuggestions(),
	component: () => <AdminBlogSuggestionsPage />,
});

// ─── Admin WhatsApp Routes ───────────────────────────────────────────────────

export const adminWhatsAppRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp",
	component: () => <AdminWhatsAppDashboardPage />,
});

export const adminWhatsAppInboxRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp/inbox",
	validateSearch: (search) => ({
		id: (search.id as number) || undefined,
	}),
	component: () => <AdminWhatsAppInboxPage />,
});

export const adminWhatsAppTemplatesRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp/templates",
	component: () => <AdminWhatsAppTemplatesPage />,
});

export const adminWhatsAppCampaignsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp/campaigns",
	component: () => <AdminWhatsAppCampaignsPage />,
});

export const adminWhatsAppCampaignCreateRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp/campaigns/create",
	validateSearch: (search) => ({
		viewId: (search.viewId as number) || undefined,
	}),
	component: () => <AdminWhatsAppCampaignCreatePage />,
});

export const adminWhatsAppSendRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/whatsapp/send",
	component: () => <AdminWhatsAppSendPage />,
});

export const adminNotificationTestRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/notifications/test",
	component: () => <AdminNotificationTestPage />,
});

export const adminSearchRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/search",
	component: () => <AdminSearchPage />,
});

export const adminSocialStudioMusicRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/social-studio/music",
	component: () => <AdminSocialStudioMusicPage />,
});

export const adminSocialStudioTemplatesRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/social-studio/templates",
	component: () => <AdminSocialStudioTemplatesPage />,
});

export const adminRoomCategoriesRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/room-categories",
	loader: () => prefetchAdminRoomCategories(),
	component: () => <AdminRoomCategoriesPage />,
});

export const adminRoomCategoryDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/room-categories/$code",
	component: () => <AdminRoomCategoryDetailPage />,
});

export const adminHomePageRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/home-page",
	component: () => <AdminHomePagePage />,
});

export const adminCalculatorConfigRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/calculator-config",
	component: () => <AdminCalculatorConfigPage />,
});

// ─── Standalone Routes (no parent layout) ────────────────────────────────────

export const onboardingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/onboarding",
	component: () => (
		<AuthGuard>
			<Suspense fallback={<FullScreenLoading />}>
				<OnboardingPage />
			</Suspense>
		</AuthGuard>
	),
});

export const acceptInvitationRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/accept-invitation",
	component: () => (
		<Suspense fallback={<FullScreenLoading />}>
			<AcceptInvitationPage />
		</Suspense>
	),
});

export const acceptTermsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/accept-terms",
	component: () => (
		<AuthGuard>
			<Suspense fallback={<FullScreenLoading />}>
				<AcceptTermsPage />
			</Suspense>
		</AuthGuard>
	),
});

// Public leadership-metrics dashboard. No auth — shared via URL with leadership.
export const internalMetricsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/internal/metrics",
	component: () => (
		<Suspense fallback={<FullScreenLoading />}>
			<InternalMetricsPage />
		</Suspense>
	),
});

// ─── Route Tree ──────────────────────────────────────────────────────────────
export const adminRrmDashboardRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm",
	component: () => <AdminRrmDashboardPage />,
});
export const adminRrmProspectsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/prospects",
	component: () => <AdminRrmProspectsPage />,
});
export const adminRrmSignupsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/signups",
	component: () => <AdminRrmSignupsPage />,
});
export const adminRrmProspectDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/prospects/$prospectId",
	component: () => <AdminRrmProspectDetailPage />,
});
export const adminRrmInboxRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/inbox",
	component: () => <AdminRrmInboxPage />,
});
export const adminRrmRampRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/ramp",
	component: () => <AdminRrmRampPage />,
});
export const adminRrmTasksRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/tasks",
	component: () => <AdminRrmTasksPage />,
});
export const adminReferralQueueRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/verify",
	component: () => <AdminReferralQueuePage />,
});
export const adminReferralsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/referrals",
	component: () => <AdminReferralsPage />,
});
// The table links here. The queue screen already renders a full referral
// detail, so it serves both: a worklist on the left, this referral on the
// right — including one that is no longer `engaged` and so is not IN the queue.
export const adminReferralDetailRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/referrals/$referralId",
	component: () => <AdminReferralQueuePage />,
});
export const adminPartnersRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/partners",
	component: () => <AdminPartnersPage />,
});
// The settle queue. Until this existed the five money endpoints had no UI at
// all — an operator could credit a partner and had no way to pay them.
// FR-I-3 asks for the funnel as a "first-class admin view", not just data.
export const adminReferralFunnelRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/funnel",
	component: () => <AdminReferralFunnelPage />,
});

export const adminPayoutsRoute = createRoute({
	getParentRoute: () => adminLayoutRoute,
	path: "/admin/rrm/payouts",
	component: () => <AdminPayoutsPage />,
});

export const routeTree = rootRoute.addChildren([
	// Pro layout (pathless) with nested pro routes
	proLayoutRoute.addChildren([
		indexRoute,
		projectsRoute,
		projectDetailsRoute,
		profileRoute,
		settingsRoute,
		securitySettingsRoute,
		teamRoute,
		analyticsRoute,
		proBlogsRoute,
		proBlogFeaturesRoute,
		proNewBlogRoute,
		proEditBlogRoute,
		websiteRoute,
		crmRoute,
		crmLeadDetailRoute,
		crmAnalyticsRoute,
		crmSettingsRoute,
		notificationsRoute,
		socialStudioRoute,
		createReelRoute,
		socialDraftDetailRoute,
	]),
	// Guest layout (pathless) with nested guest routes
	guestLayoutRoute.addChildren([
		loginRoute,
		registerRoute,
		forgotPasswordRoute,
		resetPasswordRoute,
		verifyEmailRoute,
		authRoute,
	]),
	// Admin layout (pathless) with nested admin routes
	adminLayoutRoute.addChildren([
		adminDashboardRoute,
		adminProsRoute,
		adminProDetailRoute,
		adminProAnalyticsRoute,
		adminProCompanyProfileRoute,
		adminProjectsRoute,
		adminProjectDetailRoute,
		adminImportsRoute,
		adminImportsNewRoute,
		adminImportsDetailRoute,
		adminUsersRoute,
		adminRrmDashboardRoute,
		adminRrmProspectsRoute,
		adminRrmSignupsRoute,
		adminRrmProspectDetailRoute,
		adminRrmInboxRoute,
		adminRrmRampRoute,
		adminRrmTasksRoute,
		adminReferralQueueRoute,
		adminReferralsRoute,
		adminReferralDetailRoute,
		adminPartnersRoute,
		adminPayoutsRoute,
		adminReferralFunnelRoute,
		adminUserDetailRoute,
		adminUserSecurityRoute,
		adminTaxonomyRoute,
		adminAnalyticsRoute,
		adminCommunicationsRoute,
		adminFeedbackRoute,
		adminConsultationsRoute,
		adminWebLeadsRoute,
		adminBlogsRoute,
		adminBlogCreateRoute,
		adminBlogEditRoute,
		adminBlogCategoriesRoute,
		adminBlogSuggestionsRoute,
		adminWhatsAppRoute,
		adminWhatsAppInboxRoute,
		adminWhatsAppTemplatesRoute,
		adminWhatsAppCampaignsRoute,
		adminWhatsAppCampaignCreateRoute,
		adminWhatsAppSendRoute,
		adminNotificationTestRoute,
		adminSearchRoute,
		adminSocialStudioMusicRoute,
		adminSocialStudioTemplatesRoute,
		adminRoomCategoriesRoute,
		adminRoomCategoryDetailRoute,
		adminHomePageRoute,
		adminCalculatorConfigRoute,
	]),
	// Standalone routes
	onboardingRoute,
	acceptInvitationRoute,
	acceptTermsRoute,
	internalMetricsRoute,
]);
